import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../Contexts/AuthContext/AuthContext";
import NotificationBell from "./NotificationBell";

const MOBILE_BREAKPOINT = 768;

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    logout,
    isAdmin,
    isOperator,
    isCashier,
    isManager,
    isLocationManager,
    isManagerOrAdmin,
  } = useAuth();
  const [hoveredNav, setHoveredNav] = useState(null);
  const [logoutHovered, setLogoutHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleNavClick = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  const isActive = (path) => {
    if (path === "/dashboard") {
      return location.pathname === "/" || location.pathname === "/dashboard";
    }
    return location.pathname === path;
  };

  // Nav is organised into categories rather than a flat list so the sidebar
  // scans top-to-bottom by what you're trying to do, not by what role you
  // happen to be. Cashiers only have a single action so they skip the
  // categorisation entirely.
  const navSections = isCashier()
    ? [{ items: [{ path: "/cashier", label: "Cashier", icon: "fa-shopping-cart" }] }]
    : [
        {
          title: "Overview",
          items: [
            ...(!isOperator()
              ? [{ path: "/dashboard", label: "Dashboard", icon: "fa-tachometer" }]
              : []),
          ],
        },
        {
          title: "Catalog",
          items: [
            ...(isAdmin()
              ? [
                  { path: "/products", label: "Products", icon: "fa-cubes" },
                  { path: "/product-list", label: "Inventory", icon: "fa-list" },
                ]
              : []),
          ],
        },
        {
          title: "Facilities",
          items: [
            ...(!isOperator() && (isAdmin() || isLocationManager())
              ? [{ path: "/locations", label: "Locations", icon: "fa-map-marker" }]
              : []),
            ...(!isOperator() && (isAdmin() || isManager())
              ? [{ path: "/warehouses", label: "Warehouses", icon: "fa-building" }]
              : []),
            ...(!isOperator()
              ? [{ path: "/rooms", label: "Partitions", icon: "fa-cube" }]
              : []),
          ],
        },
        {
          title: "Operations",
          items: [
            ...(isManagerOrAdmin()
              ? [{ path: "/transfers", label: "Transfers", icon: "fa-exchange" }]
              : []),
            ...(isAdmin()
              ? [{ path: "/cashier", label: "Cashier", icon: "fa-shopping-cart" }]
              : []),
            ...(isAdmin() || isOperator()
              ? [{ path: "/operators", label: "Operators", icon: "fa-cogs" }]
              : []),
          ],
        },
        {
          title: "Administration",
          items: [
            ...(isManagerOrAdmin()
              ? [{ path: "/users", label: "Users", icon: "fa-users" }]
              : []),
            ...(isAdmin()
              ? [
                  { path: "/audit-log",  label: "Log",        icon: "fa-history" },
                  { path: "/expiry-log", label: "Expiry Log", icon: "fa-calendar-times-o" },
                ]
              : []),
          ],
        },
      ].filter((section) => section.items.length > 0);

  const getRoleBadgeStyle = (role) => {
    const styles = {
      admin: { bg: "var(--accent-subtle)", color: "var(--accent)" },
      operator: { bg: "#ede9fe", color: "#7c3aed" },
      cashier: { bg: "#dcfce7", color: "#166534" },
      manager: { bg: "var(--warning-subtle)", color: "var(--warning)" },
      worker: { bg: "#e0f2fe", color: "#0284c7" },
      viewer: { bg: "var(--bg-tertiary)", color: "var(--text-secondary)" },
    };
    return styles[role] || styles.viewer;
  };

  const roleStyle = getRoleBadgeStyle(user?.role);

  // Visual tokens tuned for the compact redesign — tight but breathable,
  // stronger active-state cue (inset accent bar), gradient branding.
  const SIDEBAR_W = isMobile ? 252 : 232;
  const LOGO_H = isMobile ? 48 : 56;

  const sidebarContent = (
    <nav
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "flex-start",
        height: "100vh",
        minHeight: 0,
        width: `${SIDEBAR_W}px`,
        minWidth: `${SIDEBAR_W}px`,
        backgroundColor: "var(--bg-primary)",
        borderRight: "1px solid var(--border-primary)",
        padding: "0 var(--space-3)",
        zIndex: 100,
        ...(isMobile
          ? {
              position: "fixed",
              top: 0,
              left: 0,
              transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
              transition: "transform 250ms ease-in-out",
              boxShadow: mobileOpen ? "4px 0 24px rgba(0,0,0,0.15)" : "none",
            }
          : {}),
      }}
    >
      {/* Brand block — compact, gradient wordmark for a polished feel.
          The notification bell lives in the top-right so it's always
          reachable without taking a full nav row. */}
      <div
        style={{
          position: "relative",
          width: "100%",
          flexShrink: 0,
          display: "flex",
          justifyContent: isMobile ? "space-between" : "center",
          alignItems: "center",
          gap: "var(--space-2)",
          padding: "var(--space-4) var(--space-2)",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "var(--space-3)",
            right: "var(--space-2)",
            zIndex: 5,
          }}
        >
          <NotificationBell />
        </div>
        <div
          onClick={() => handleNavClick("/")}
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            flex: 1,
            gap: "8px",
          }}
        >
          <img
            src="/images/LogoOnly.svg"
            alt="RFID Inventory System"
            style={{
              height: `${LOGO_H}px`,
              width: "auto",
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.06))",
            }}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "2px",
              lineHeight: 1.1,
            }}
          >
            <span
              style={{
                fontSize: "11px",
                fontWeight: "var(--weight-semibold)",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "var(--text-tertiary)",
              }}
            >
              RFID
            </span>
            <span
              style={{
                fontSize: "14px",
                fontWeight: "var(--weight-bold)",
                letterSpacing: "0.02em",
                textAlign: "center",
                whiteSpace: "nowrap",
                backgroundImage: "linear-gradient(135deg, #2B6CB0 0%, #4A5568 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              Inventory System
            </span>
          </div>
        </div>
        {isMobile && (
          <button
            onClick={() => setMobileOpen(false)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "var(--space-2)",
              color: "var(--text-secondary)",
              fontSize: "18px",
              borderRadius: "var(--radius-md)",
            }}
            aria-label="Close menu"
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Nav items — categorised. Each section has a tiny uppercase label
          (skipped if only one section) with a hairline rule underneath. */}
      <div
        style={{
          width: "100%",
          flex: "1 1 0",
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "stretch",
          borderTop: "1px solid var(--border-primary)",
          paddingTop: "var(--space-3)",
          paddingBottom: "var(--space-2)",
          gap: "12px",
        }}
      >
        {navSections.map((section, sIdx) => (
          <div
            key={section.title || `section-${sIdx}`}
            style={{ display: "flex", flexDirection: "column", gap: "2px" }}
          >
            {section.title && navSections.length > 1 && (
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: "var(--weight-semibold)",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--text-tertiary)",
                  padding: "4px 12px 6px",
                  marginTop: sIdx === 0 ? 0 : "4px",
                }}
              >
                {section.title}
              </div>
            )}
            {section.items.map(({ path, label, icon }) => {
              const active = isActive(path);
              const hovered = hoveredNav === path;

              return (
                <button
                  key={path}
                  onClick={() => handleNavClick(path)}
                  onMouseEnter={() => setHoveredNav(path)}
                  onMouseLeave={() => setHoveredNav(null)}
                  style={{
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    gap: "12px",
                    padding: "9px 12px",
                    borderRadius: "var(--radius-md)",
                    border: "none",
                    backgroundColor: active
                      ? "var(--accent-subtle)"
                      : hovered
                        ? "var(--bg-tertiary)"
                        : "transparent",
                    color: active
                      ? "var(--text-primary-active)"
                      : hovered
                        ? "var(--text-primary)"
                        : "var(--text-secondary)",
                    fontWeight: active
                      ? "var(--weight-semibold)"
                      : "var(--weight-medium)",
                    fontSize: "var(--text-sm)",
                    cursor: "pointer",
                    textAlign: "left",
                    // Inset "pill indicator" on the left for the active route —
                    // doesn't affect layout so inactive/active items stay aligned.
                    boxShadow: active ? "inset 3px 0 0 var(--text-primary-active)" : "none",
                    transition: "background-color 120ms ease, color 120ms ease, box-shadow 120ms ease, transform 120ms ease",
                    transform: hovered && !active ? "translateX(2px)" : "translateX(0)",
                  }}
                >
                  <i
                    className={`fa ${icon}`}
                    style={{
                      fontSize: "15px",
                      width: "18px",
                      textAlign: "center",
                      color: active
                        ? "var(--text-primary-active)"
                        : hovered
                          ? "var(--text-primary)"
                          : "var(--text-tertiary)",
                      transition: "color 120ms ease",
                    }}
                    aria-hidden="true"
                  />
                  <span style={{ flex: 1 }}>{label}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Footer — user chip + logout. Compact, single-row chip. */}
      <div
        style={{
          display: "flex",
          width: "100%",
          flexShrink: 0,
          flexDirection: "column",
          alignItems: "stretch",
          gap: "var(--space-2)",
          paddingTop: "var(--space-3)",
          paddingBottom: "var(--space-4)",
          borderTop: "1px solid var(--border-primary)",
        }}
      >
        <div
          style={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            gap: "10px",
            padding: "8px 10px",
            borderRadius: "var(--radius-md)",
            backgroundColor: "var(--bg-secondary)",
            border: "1px solid var(--border-primary)",
          }}
        >
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "999px",
              background: "linear-gradient(135deg, var(--accent-subtle), var(--bg-primary))",
              border: "1px solid var(--border-primary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--accent)",
              flexShrink: 0,
            }}
          >
            <i className="fa fa-user" style={{ fontSize: "12px" }} aria-hidden="true" />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minWidth: 0,
              lineHeight: 1.2,
            }}
          >
            <span
              style={{
                fontSize: "13px",
                fontWeight: "var(--weight-semibold)",
                color: "var(--text-primary)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user?.username || "User"}
            </span>
            <span
              style={{
                fontSize: "10.5px",
                fontWeight: "var(--weight-medium)",
                color: roleStyle.color,
                backgroundColor: roleStyle.bg,
                padding: "1px 6px",
                borderRadius: "999px",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                lineHeight: 1.5,
                alignSelf: "flex-start",
                marginTop: "2px",
              }}
            >
              {user?.role || "viewer"}
            </span>
          </div>
        </div>

        <button
          onClick={handleLogout}
          onMouseEnter={() => setLogoutHovered(true)}
          onMouseLeave={() => setLogoutHovered(false)}
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
            padding: "8px 10px",
            borderRadius: "var(--radius-md)",
            border: `1px solid ${logoutHovered ? "var(--error)" : "var(--border-primary)"}`,
            backgroundColor: logoutHovered ? "var(--error-subtle)" : "transparent",
            color: logoutHovered ? "var(--error)" : "var(--text-secondary)",
            fontSize: "13px",
            fontWeight: "var(--weight-medium)",
            cursor: "pointer",
            transition: "all 120ms ease",
          }}
        >
          <i className="fa fa-sign-out" style={{ fontSize: "13px" }} aria-hidden="true" />
          Logout
        </button>
      </div>
    </nav>
  );

  return (
    <>
      {/* Mobile hamburger button */}
      {isMobile && !mobileOpen && (
        <button
          onClick={() => setMobileOpen(true)}
          style={{
            position: "fixed",
            top: "var(--space-4)",
            left: "var(--space-4)",
            zIndex: 99,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "44px",
            height: "44px",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-primary)",
            backgroundColor: "var(--bg-primary)",
            color: "var(--text-primary)",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            fontSize: "20px",
          }}
          aria-label="Open menu"
        >
          <i className="fa fa-bars" aria-hidden="true" />
        </button>
      )}

      {/* Backdrop overlay on mobile */}
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.4)",
            zIndex: 99,
            transition: "opacity 250ms ease-in-out",
          }}
        />
      )}

      {/* Sidebar */}
      {isMobile ? sidebarContent : sidebarContent}
    </>
  );
};

export default Navbar;
